import React, { useCallback, useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { getAppSummary } from "../api";
import DateRangeFilter, { presetRange } from "../components/DateRangeFilter";

const STATE_COLORS = {
  ACTIVE: "#16a34a", COMPLETED: "#16a34a", SUCCESS: "#16a34a",
  INITIATED: "#2563eb", ACTIVATION_IN_PROGRESS: "#2563eb", PENDING: "#2563eb", SCHEDULED: "#2563eb", IN_PROGRESS: "#2563eb", NOTIFICATION_IN_PROGRESS: "#2563eb",
  FAILED: "#dc2626", REVOKED: "#dc2626", CANCELLED: "#dc2626",
  PAUSED: "#d97706",
};

function rupees(paise) {
  return `₹${((paise || 0) / 100).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

// Full precision for tooltips — the cards round to whole rupees to stay readable.
function rupeesExact(paise) {
  return `₹${((paise || 0) / 100).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function num(n) {
  return (n || 0).toLocaleString("en-IN");
}

// ACTIVATION_IN_PROGRESS -> "Activation in progress"
function humanize(state) {
  return state.charAt(0) + state.slice(1).toLowerCase().replace(/_/g, " ");
}

function StatCard({ label, value, title, subLines, accent }) {
  return (
    <div className="ar-card" style={{ ...sc.card, borderTop: `3px solid ${accent || "#4f46e5"}` }}>
      <div style={sc.label}>{label}</div>
      <div style={sc.value} title={title}>{value}</div>
      {(subLines || []).filter(Boolean).map((line, i) => (
        <div key={i} style={sc.sub}>{line}</div>
      ))}
    </div>
  );
}

function StateBreakdown({ title, byState, total, valueByState, note }) {
  const entries = Object.entries(byState).sort((a, b) => b[1] - a[1]);
  return (
    <div style={bd.box}>
      <div style={bd.title}>{title}</div>
      {total === 0 ? (
        <div style={bd.empty}>Nothing in this window.</div>
      ) : (
        <>
          <div style={bd.bars}>
            {entries.map(([state, count]) => {
              const pct = total > 0 ? (count / total) * 100 : 0;
              const color = STATE_COLORS[state] || "#94a3b8";
              return (
                <div key={state} style={bd.row}>
                  <div style={{ ...bd.dot, backgroundColor: color }} />
                  <span style={bd.stateName} title={state}>{humanize(state)}</span>
                  <div style={bd.barTrack}>
                    <div style={{ ...bd.barFill, width: `${pct}%`, backgroundColor: color }} />
                  </div>
                  <span style={bd.count}>{num(count)}</span>
                  <span style={bd.pct}>{pct >= 0.1 ? `${pct.toFixed(0)}%` : "<1%"}</span>
                  {valueByState && (
                    <span style={bd.value} title={rupeesExact(valueByState[state])}>
                      {rupees(valueByState[state])}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
          {note && <div style={bd.note}>{note}</div>}
        </>
      )}
    </div>
  );
}

function SummarySection({ data }) {
  const activeCount = data.subscriptions_by_state["ACTIVE"] || 0;
  const successPayments = data.transactions_by_state["COMPLETED"] || 0;
  const setupCount = data.trial_setups_count || 0;
  const upcoming = data.scheduled_by_state["SCHEDULED"] || 0;
  const activeShare = data.subscriptions_total > 0
    ? ((activeCount / data.subscriptions_total) * 100).toFixed(0)
    : null;

  return (
    <>
      <div style={s.statsGrid}>
        <StatCard
          label="Total Subscriptions"
          value={num(data.subscriptions_total)}
          subLines={["includes archived"]}
          accent="#4f46e5"
        />
        <StatCard
          label="Active"
          value={num(activeCount)}
          subLines={[activeShare != null ? `${activeShare}% of subscriptions created` : null]}
          accent="#16a34a"
        />
        <StatCard
          label="Revenue Collected"
          value={rupees(data.revenue_collected_paise)}
          title={rupeesExact(data.revenue_collected_paise)}
          subLines={[
            `${num(successPayments)} payments · ${rupees(data.payment_revenue_collected_paise)}`,
            `${num(setupCount)} setups · ${rupees(data.trial_revenue_collected_paise)}`,
          ]}
          accent="#0891b2"
        />
        <StatCard
          label="Upcoming Payments"
          value={rupees(data.scheduled_value_by_state_paise?.["SCHEDULED"])}
          title={rupeesExact(data.scheduled_value_by_state_paise?.["SCHEDULED"])}
          subLines={[
            `${num(upcoming)} scheduled`,
            `${rupees(data.scheduled_value_paise)} across ${num(data.scheduled_total)} total`,
          ]}
          accent="#d97706"
        />
      </div>

      <div style={s.breakdowns}>
        <StateBreakdown
          title="Subscriptions by state"
          byState={data.subscriptions_by_state}
          total={data.subscriptions_total}
          note="Failed includes archived subscriptions."
        />
        <StateBreakdown
          title="Transactions by state"
          byState={data.transactions_by_state}
          total={data.transactions_total}
        />
        <StateBreakdown
          title="Scheduled payments by state"
          byState={data.scheduled_by_state}
          total={data.scheduled_total}
          valueByState={data.scheduled_value_by_state_paise}
        />
      </div>
    </>
  );
}

function Skeleton() {
  return (
    <>
      <div style={s.statsGrid}>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} style={{ ...sc.card, borderTop: "3px solid #e2e8f0" }}>
            <div className="ar-pulse" style={{ ...sk.bar, width: "55%" }} />
            <div className="ar-pulse" style={{ ...sk.bar, height: 26, width: "70%", marginTop: 12 }} />
            <div className="ar-pulse" style={{ ...sk.bar, width: "85%", marginTop: 12 }} />
          </div>
        ))}
      </div>
      <div style={s.breakdowns}>
        {[0, 1, 2].map((i) => (
          <div key={i} style={bd.box}>
            <div className="ar-pulse" style={{ ...sk.bar, width: "45%" }} />
            {[0, 1, 2, 3].map((j) => (
              <div key={j} className="ar-pulse" style={{ ...sk.bar, marginTop: 14 }} />
            ))}
          </div>
        ))}
      </div>
    </>
  );
}

export default function AppSummary() {
  const { app } = useOutletContext();
  const [range, setRange] = useState(() => presetRange(0)); // default: today
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);

  const { start, end } = range;
  // Empty bounds are the "All" preset, not an invalid range.
  const invalidRange = Boolean(start && end && start > end);

  const load = useCallback(async () => {
    if (start && end && start > end) return;
    setLoading(true);
    setError(null);
    try {
      setSummary(await getAppSummary(app.id, { start_date: start || null, end_date: end || null }));
      setUpdatedAt(new Date());
    } catch (e) {
      setError(e.message || "Could not load the summary.");
    } finally {
      setLoading(false);
    }
  }, [app.id, start, end]);

  useEffect(() => { load(); }, [load]);

  const isEmpty = summary
    && summary.subscriptions_total === 0
    && summary.transactions_total === 0
    && summary.scheduled_total === 0;

  return (
    <div>
      <style>{CSS}</style>

      <div style={s.pageHead}>
        <div>
          <h1 style={s.h1}>Summary</h1>
          <p style={s.sub}>{app.name} · {app.phonepe_environment}</p>
        </div>
        <div style={s.headRight}>
          {updatedAt && !loading && (
            <span style={s.updatedAt}>
              Updated {updatedAt.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
            </span>
          )}
          <button className="ar-btn" onClick={load} disabled={loading} style={s.refreshBtn}>
            {loading ? "Refreshing…" : "↻ Refresh"}
          </button>
        </div>
      </div>

      {/* Date filter */}
      <div style={s.filters}>
        <DateRangeFilter value={range} onChange={setRange} showWindowLabel />
      </div>

      {invalidRange && (
        <div style={s.errorBox}>
          <span>Start date must not be after end date.</span>
        </div>
      )}

      {error && (
        <div style={s.errorBox}>
          <span>{error}</span>
          <button className="ar-btn" onClick={load} style={s.retryBtn}>Try again</button>
        </div>
      )}

      {loading && !summary ? (
        <Skeleton />
      ) : !summary ? null : isEmpty ? (
        <div style={s.empty}>
          <div style={s.emptyIcon}>📭</div>
          <p style={s.emptyText}>No activity in this window.</p>
          <p style={s.emptyHint}>Try a wider date range, or integrate using the URLs in Settings.</p>
        </div>
      ) : (
        <SummarySection data={summary} />
      )}
    </div>
  );
}

const CSS = `
@keyframes ar-pulse { 0%,100% { opacity: 1 } 50% { opacity: .45 } }
.ar-pulse { animation: ar-pulse 1.4s ease-in-out infinite; }
.ar-card { transition: box-shadow .15s, transform .15s; }
.ar-card:hover { box-shadow: 0 4px 14px rgba(0,0,0,0.10); transform: translateY(-1px); }
.ar-btn { transition: background-color .15s, border-color .15s; }
.ar-btn:hover:not(:disabled) { background-color: #f8fafc; border-color: #cbd5e1; }
.ar-btn:disabled { opacity: .6; cursor: not-allowed; }
@media (max-width: 700px) {
  .ar-card { padding: 16px 18px !important; }
}
`;

const s = {
  pageHead: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 16 },
  headRight: { display: "flex", alignItems: "center", gap: 10 },
  updatedAt: { fontSize: 12, color: "#94a3b8" },
  refreshBtn: { padding: "7px 14px", backgroundColor: "#fff", color: "#475569", border: "1px solid #e2e8f0", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 600 },
  h1: { margin: "0 0 4px", fontSize: 22, color: "#1e293b" },
  sub: { margin: 0, color: "#64748b", fontSize: 13 },
  filters: { display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 22 },
  errorBox: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", padding: "12px 16px", backgroundColor: "#fee2e2", color: "#991b1b", borderRadius: 10, fontSize: 13, marginBottom: 20 },
  retryBtn: { padding: "6px 12px", backgroundColor: "#fff", color: "#991b1b", border: "1px solid #fecaca", borderRadius: 8, cursor: "pointer", fontSize: 12.5, fontWeight: 600 },
  statsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 16, marginBottom: 28 },
  breakdowns: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 16, marginBottom: 32 },
  empty: { textAlign: "center", padding: "60px 0" },
  emptyIcon: { fontSize: 36 },
  emptyText: { color: "#1e293b", marginTop: 10, marginBottom: 4, fontSize: 15, fontWeight: 600 },
  emptyHint: { color: "#64748b", margin: 0, fontSize: 13 },
};

const sc = {
  card: { backgroundColor: "#fff", borderRadius: 10, padding: "20px 22px", boxShadow: "0 1px 3px rgba(0,0,0,0.07)" },
  label: { fontSize: 12, color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" },
  value: { fontSize: 30, fontWeight: 700, color: "#1e293b", marginTop: 6, lineHeight: 1.15 },
  sub: { fontSize: 12, color: "#94a3b8", marginTop: 4 },
};

const bd = {
  box: { backgroundColor: "#fff", borderRadius: 10, padding: "20px 22px", boxShadow: "0 1px 3px rgba(0,0,0,0.07)" },
  title: { fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 14 },
  bars: { display: "flex", flexDirection: "column", gap: 10 },
  row: { display: "flex", alignItems: "center", gap: 8 },
  dot: { width: 8, height: 8, borderRadius: "50%", flexShrink: 0 },
  stateName: { fontSize: 12, color: "#475569", width: 132, flexShrink: 0 },
  barTrack: { flex: 1, minWidth: 30, height: 6, backgroundColor: "#f1f5f9", borderRadius: 3, overflow: "hidden" },
  barFill: { height: "100%", borderRadius: 3, transition: "width 0.3s" },
  count: { fontSize: 12, color: "#1e293b", fontWeight: 600, width: 52, textAlign: "right", flexShrink: 0 },
  pct: { fontSize: 11, color: "#94a3b8", width: 34, textAlign: "right", flexShrink: 0 },
  value: { fontSize: 12, color: "#64748b", width: 84, textAlign: "right", flexShrink: 0 },
  note: { fontSize: 11, color: "#94a3b8", marginTop: 14, paddingTop: 12, borderTop: "1px solid #f1f5f9" },
  empty: { fontSize: 13, color: "#cbd5e1", padding: "8px 0 4px" },
};

const sk = {
  bar: { height: 10, borderRadius: 6, backgroundColor: "#eef2f7" },
};
